import { useEffect, useLayoutEffect, useRef } from 'react';
import { delivered } from '@day';
import { useStore } from './store';
import { backFromStop, backToDay, besideMap, closeRec, frame, go, onMapPage, openStop, planWith, setMap, theme, ui, hideSetup, type Page } from './ui';
import { menuKeys, menuOpen, wireMenus } from './menu';
import { useMenus } from './menus';
import { useDrag } from './drag';
import { dialogs } from './dialogs';
import { CommandBar, cmdk, toggleCmdk } from '../ui/CommandBar';
import { Dialogs } from '../ui/Dialogs';
import { openDialog } from './dialogs';
import { dayWords, streetOf } from './words';
import { keyOf, routeTitle, type Model } from '../data/model';
import { Dot } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { Mark } from '../ui/Mark';
import { toast } from '../ui/Overlays';
import { Live } from '../pages/Live';
import { StopsPage } from '../pages/Stops';
import { CouriersPage, CourierRec } from '../pages/Couriers';
import { ReportsPage } from '../pages/Reports';
import { PrintSheet } from '../pages/RouteSheet';
import { StopRecord } from '../pages/StopBody';
import { WaitingRecord } from '../pages/Waiting';
import { NotesPanel } from '../ui/Notes';
import { stopsRows } from '../pages/Stops';
import { commandHeld, commandKeys } from './platform';
import { track } from '../data/analytics';

/** One frame on every page: the icon rail, the top bar, the page (control-room-web.html, "The frame"). */

const PAGES: [Page, string, string][] = [
  ['live', 'mapTrifold', 'Live'],
  ['stops', 'mapPin', 'Stops'],
  ['routes', 'path', 'Routes'],
  ['couriers', 'users', 'Couriers'],
  ['reports', 'chartBar', 'Analytics'],
];

function Rail() {
  const s = useStore(ui);
  return (
    <nav id="rail" aria-label="Pages">
      <Mark className="mark" />
      {PAGES.map(([k, i, l]) => (
        <button key={k} className="nav" data-tip={l} data-side="right" aria-label={l} aria-current={s.page === k ? 'page' : 'false'} onClick={() => go(k)}>
          <Icon name={s.page === k ? `${i}Fill` : i} />
        </button>
      ))}
      <button className="nav end" data-tip={s.theme === 'dark' ? 'Light' : 'Dark'} data-side="right" aria-label="Light or dark" onClick={theme}>
        <Icon name={s.theme === 'dark' ? 'sun' : 'moon'} />
      </button>
      <button className="nav" data-menu="settings:" data-tip="Settings" data-side="right" aria-label="Settings">
        <Icon name="gear" />
      </button>
    </nav>
  );
}

function Top({ m }: { m: Model }) {
  const s = useStore(ui);
  const caret = <Icon name="caretRight" size="sm" className="ink3" />;
  const r = s.route ? m.route(s.route) : null;
  const stop = s.view === 'stop' && s.stop ? m.stop(s.stop) : null;
  const p = s.view === 'person' && s.person ? m.person(s.person) : null;
  const rp = r ? m.person(r.riderId) : null;
  let crumbs;
  const title = PAGES.find((x) => x[0] === s.page)![2];
  const past = s.page === 'routes' && s.rday && s.rday < m.today ? s.rday : null;
  if (!onMapPage(s.page)) crumbs = <span className="title">{title}</span>;
  else if (s.view === 'day' || (!r && !p))
    crumbs = (
      <>
        <span className="title">{title}</span>
        {caret}
        <span className="crumb here" data-tip={m.business?.zoneConfirmed ? `The business’s day, ${m.zone}` : `This computer’s day: the business hasn’t confirmed where it works`}>
          {past ? dayWords(past) : `Today, ${dayWords(m.today)}`}
        </span>
      </>
    );
  else
    crumbs = (
      <>
        <span className="title">{title}</span>
        {caret}
        <button className="crumb today" onClick={backToDay}>
          {past ? dayWords(past) : 'Today'}
        </button>
        {caret}
        {p ? (
          <span className="crumb here">{p.first}</span>
        ) : (
          r && (
            <>
              <button className={`crumb route${stop ? '' : ' here'}`} data-menu="routes:">
                <Dot p={rp} />
                <span className="lbl">
                  {rp?.first ?? 'A courier'} · {routeTitle(r, m.zone)}
                </span>
                <Icon name="caretDown" size="sm" />
              </button>
              {stop && (
                <>
                  {caret}
                  <span className="crumb street here" data-tip={stop.address}>
                    <span className="lbl">{streetOf(stop)}</span>
                  </span>
                </>
              )}
            </>
          )
        )}
      </>
    );
  const unread = m.data.notes.filter((x) => !x.read).length;
  const biz = m.business?.name ?? 'Your business';
  const initials = biz
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <header id="top">
      <div id="crumbs">{crumbs}</div>
      {s.page === 'live' && (
        <div className="seg" role="group" aria-label="Map or list">
          <button aria-pressed={s.map} data-tip="The map beside the list · M" onClick={() => setMap(true)}>
            <Icon name="mapTrifold" />
            Map
          </button>
          <button aria-pressed={!s.map} data-tip="Only the list · M" onClick={() => setMap(false)}>
            <Icon name="list" />
            List
          </button>
        </div>
      )}
      <button
        className="search"
        data-act="cmdk"
        onClick={() => {
          track('command_bar_opened', { via: 'button' });
          toggleCmdk(true);
        }}
      >
        <Icon name="magnifyingGlass" />
        <span className="grow">Search or type a command</span>
        <span className="kbd">{commandKeys}</span>
      </button>
      <button className="btn primary" onClick={() => planWith()}>
        <Icon name="path" />
        Plan a route
      </button>
      <button className="iconbtn" data-tip="Notifications" aria-label="Notifications" aria-expanded={s.notes} onClick={() => ui.set({ notes: !s.notes })}>
        <Icon name="bell" size="lg" />
        {unread > 0 && (
          <span className="n" id="belln">
            {unread}
          </span>
        )}
      </button>
      {s.notes && <NotesPanel m={m} />}
      <span className="vr" />
      <button className="me" data-menu="me:" data-ctx="me:" aria-label={`${m.ownerName ?? 'You'}, owner of ${biz}`}>
        {m.business?.logoUrl ? <img className="logo" src={m.business.logoUrl} alt="" /> : <span className="logo">{initials}</span>}
        <span className="av sm" style={{ '--c': 'var(--ink2)', '--on': 'var(--surface)' } as React.CSSProperties}>
          {(m.ownerName ?? 'Y').slice(0, 1).toUpperCase()}
        </span>
        <Icon name="caretDown" size="sm" className="ink3" />
      </button>
    </header>
  );
}

/** The record docked beside a table: a stop's or a courier's. */
function Record({ m }: { m: Model }) {
  const s = useStore(ui);
  const show = !!s.rec && !besideMap(s);
  if (!show || !s.rec) return <aside id="record" aria-label="Record" />;
  if (s.rec.type === 'waiting') {
    const w = m.waiting.find((x) => x.id === s.rec!.id);
    if (!w) return <aside id="record" aria-label="Record" />;
    return (
      <aside id="record" className="on" aria-label="Record">
        <div className="in" id="recordIn">
          <WaitingRecord m={m} w={w} close={closeRec} />
        </div>
      </aside>
    );
  }
  if (s.rec.type === 'stop') {
    const st = m.stop(s.rec.id);
    if (!st) return <aside id="record" aria-label="Record" />;
    return (
      <aside id="record" className="on" aria-label="Record">
        <div className="in" id="recordIn">
          <StopRecord
            m={m}
            s={st}
            close={closeRec}
            tools={
              <button
                className="iconbtn sm"
                data-tip="Show on the map"
                aria-label="Show on the map"
                onClick={() => {
                  ui.set({ page: 'live', map: true, rec: null });
                  openStop(keyOf(st));
                  frame();
                }}
              >
                <Icon name="mapTrifold" />
              </button>
            }
          />
        </div>
      </aside>
    );
  }
  const p = m.person(s.rec.id);
  if (!p) return <aside id="record" aria-label="Record" />;
  return (
    <aside id="record" className="on" aria-label="Record">
      <div className="in" id="recordIn">
        <CourierRec m={m} p={p} />
      </div>
    </aside>
  );
}

/** One card in the corner: what's left of setting up (setup_list.dart), from what's stored. */
function Setup({ m, docked }: { m: Model; docked: boolean }) {
  const s = useStore(ui);
  const box = useRef<HTMLDivElement>(null);
  const steps: { done: boolean; label: string; note: string; how: string; run?: () => void }[] = [
    // Done once the business has a place (`places` not empty), as setup_list.dart has it.
    { done: !!m.place, label: 'Where routes start', note: 'Your shop, so every plan starts there', how: '', run: () => openDialog({ kind: 'startPlace' }) },
    { done: m.people.length > 0, label: 'Invite a courier', note: 'A link they open in the Routr app', how: '', run: () => openDialog({ kind: 'invite' }) },
    { done: !!m.business?.logoUrl, label: 'Add your logo', note: 'Customers see it on their tracking page', how: 'Add your logo in the Routr app: Settings, then Your business.' },
    { done: !!m.business?.payments.chosen, label: 'Payment at the door', note: 'Cash, transfer or POS, or switch it off', how: 'Payment at the door is in the Routr app: Settings, then Payments.' },
    { done: m.routes.some((r) => r.sentAt != null) || (m.history ?? []).some((r) => r.sentAt != null), label: 'Send a first route', note: 'It waits on their map until they start', how: 'Send a route from the Routr app for now: Live, then Plan a route.' },
  ];
  const left = steps.filter((x) => !x.done);
  const show = s.setup && !docked && left.length > 0 && m.ready;
  useLayoutEffect(() => {
    const main = document.getElementById('main');
    main?.style.setProperty('--corner', show && box.current ? `${box.current.offsetHeight + 32}px` : '0px');
  });
  if (!show) return null;
  const next = left[0];
  const done = steps.length - left.length;
  return (
    <div id="setup" ref={box}>
      <div className="row">
        <b className="grow">Finish setting up</b>
        <span className="mono cap">
          {done} of {steps.length}
        </span>
        <button className="iconbtn sm" data-tip="Hide" aria-label="Hide" onClick={hideSetup}>
          <Icon name="x" size="sm" />
        </button>
      </div>
      <div className="bar" style={{ height: 4, margin: '6px 0 10px' }}>
        <i style={{ width: `${(done / steps.length) * 100}%`, background: 'var(--ink)' }} />
      </div>
      <button className="link" onClick={() => (next.run ? next.run() : toast(next.how, { ms: 6000 }))}>
        {next.label}
        <Icon name="caretRight" size="sm" />
      </button>
      <div className="cap" style={{ marginTop: 2 }}>
        {next.note}
      </div>
    </div>
  );
}

/** The keys (control-room-web.html, "Keys"): Esc steps back, M the map, F frames, ↑ ↓ the stops, / searches. */
function useKeys(m: Model) {
  const latest = useRef(m);
  latest.current = m;
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const s = ui.get();
      const a = document.activeElement as HTMLElement | null;
      const typing = a?.tagName === 'INPUT' || a?.tagName === 'TEXTAREA';
      // ⌘K on a Mac, Ctrl K on Windows and the rest, as each system's own apps take it.
      if (commandHeld(e) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!cmdk.get().open) track('command_bar_opened', { via: 'keys' });
        return toggleCmdk();
      }
      // A dialog or ⌘K has the keys while it's open; a menu, while it's open.
      if (dialogs.get().open || cmdk.get().open) return;
      if (menuOpen() && menuKeys(e)) return;
      if (e.key === 'Escape') {
        if (typing) {
          const i = a as HTMLInputElement;
          if (i.value) return;
          return i.blur();
        }
        if (s.noting) return ui.set({ noting: null });
        if (s.drawing) return ui.set({ drawing: null });
        if (s.reshaping) return ui.set({ reshaping: false });
        if (s.page === 'couriers' && s.zone) return ui.set({ zone: null, renaming: false });
        if (s.sel.size) return ui.set({ sel: new Set() });
        if (s.rec) return closeRec();
        if (onMapPage(s.page) && s.view === 'stop') return backFromStop();
        if (onMapPage(s.page) && s.view !== 'day') return backToDay();
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const m = latest.current;
      if (e.key === '/') {
        const f = (document.getElementById('routeq') ?? document.getElementById('routesq') ?? (s.page === 'stops' ? document.getElementById('stopq') : null) ?? (s.page === 'couriers' ? document.getElementById('courq') : null)) as HTMLInputElement | null;
        if (f) {
          e.preventDefault();
          f.focus();
        }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const d = e.key === 'ArrowDown' ? 1 : -1;
        if (besideMap(s) && s.view === 'stop' && s.stop) {
          const st = m.stop(s.stop);
          const r = st ? m.route(st.routeId) : null;
          const i = r ? r.stops.findIndex((x) => x.id === st!.id) : -1;
          const x = r?.stops[i + d];
          if (x) {
            e.preventDefault();
            openStop(keyOf(x), s.back);
          }
        } else if (s.rec?.type === 'stop') {
          const rows = s.page === 'stops' ? stopsRows(m, s).rows : (m.route(s.route ?? '')?.stops ?? []);
          const i = rows.findIndex((x) => keyOf(x) === s.rec!.id);
          const x = rows[i + d];
          if (x) {
            e.preventDefault();
            ui.set({ rec: { type: 'stop', id: keyOf(x) } });
            document.querySelector(`tr[data-stop="${CSS.escape(keyOf(x))}"]`)?.scrollIntoView({ block: 'nearest' });
          }
        } else if (s.rec?.type === 'courier') {
          const i = m.people.findIndex((p) => p.uid === s.rec!.id);
          const x = m.people[i + d];
          if (x) {
            e.preventDefault();
            ui.set({ rec: { type: 'courier', id: x.uid } });
          }
        }
        return;
      }
      if (onMapPage(s.page) && e.key.toLowerCase() === 'f') return frame();
      if (s.page === 'live' && e.key.toLowerCase() === 'm') return setMap(!s.map);
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);
}

export function Shell({ m }: { m: Model }) {
  const s = useStore(ui);
  useKeys(m);
  useMenus();
  useDrag();
  useEffect(() => wireMenus(), []);
  useEffect(() => {
    document.documentElement.dataset.theme = s.theme;
  }, [s.theme]);
  const docked = !!s.rec && !besideMap(s);
  return (
    <>
      <div id="app">
        <Rail />
        <Top m={m} />
        <main id="main" className={docked ? 'docked' : ''}>
          {!m.ready ? (
            <div className="loading" role="status">
              {m.error ? `Couldn’t read today’s routes: ${m.error}` : 'Loading today…'}
            </div>
          ) : (
            <>
              {onMapPage(s.page) && <Live m={m} />}
              {s.page === 'couriers' && (
                <section className="page on" id="p-couriers">
                  <CouriersPage m={m} />
                </section>
              )}
              {s.page === 'stops' && (
                <section className="page on" id="p-stops">
                  <StopsPage m={m} />
                </section>
              )}
              {s.page === 'reports' && (
                <section className="page on" id="p-reports">
                  <ReportsPage m={m} />
                </section>
              )}
              <Record m={m} />
            </>
          )}
        </main>
      </div>
      <Setup m={m} docked={docked} />
      <CommandBar m={m} />
      <Dialogs m={m} />
      <PrintSheet />
    </>
  );
}

export { delivered };
